import React, { useState } from 'react';
import { api } from '../../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog, DialogContent, DialogDescription, DialogHeader, 
  DialogTitle, DialogFooter 
} from '@/components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import { toast } from 'sonner';
import { UserCheck, Ban, Clock, Trash2, Shield, Gift, UserX, KeyRound, Key } from 'lucide-react';

export default function UserActionsModal({ user, currentUser, onClose }) {
  const [action, setAction] = useState('');
  const [duration, setDuration] = useState('365');
  const [banReason, setBanReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  
  // Check if current user is superuser (maridjo@gmail.com)
  const isSuperuser = currentUser?.email === 'maridjo@gmail.com';

  const handleGenerateActivationCode = async () => {
    setLoading(true);
    try {
      const response = await api.post('/admin/generate-activation-code', {
        user_id: user.id,
        subscription_days: parseInt(duration)
      });
      setGeneratedCode(response.data.activation_code);
      toast.success(`Activation code generated and sent to ${user.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate activation code');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateSubscription = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/users/${user.id}/activate?duration_days=${duration}`);
      toast.success('Subscription activated successfully');
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleBanUser = async () => {
    if (!banReason.trim()) {
      toast.error('Please provide a reason for banning');
      return;
    }
    
    setLoading(true);
    try {
      await api.put(`/admin/users/${user.id}/ban`, null, {
        params: { reason: banReason }
      });
      toast.success('User banned successfully');
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban user');
    } finally {
      setLoading(false);
    }
  };

  const handleUnbanUser = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/users/${user.id}/unban`);
      toast.success('User unbanned successfully');
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unban user');
    } finally {
      setLoading(false);
    }
  };

  const handleExtendTrial = async () => {
    const days = parseInt(duration);
    if (isNaN(days) || days <= 0) {
      toast.error('Please enter a valid number of days');
      return;
    }
    
    setLoading(true);
    try {
      await api.put(`/admin/users/${user.id}/extend-trial?additional_days=${days}`);
      toast.success(`Trial extended by ${days} days`);
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend trial');
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteToAdmin = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/users/${user.id}/promote-admin`);
      toast.success(`${user.full_name} promoted to admin successfully`);
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote user');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoteFromAdmin = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/users/${user.id}/demote-admin`);
      toast.success(`${user.full_name} demoted from admin successfully`);
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to demote user');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeSubscription = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/users/${user.id}/revoke-subscription`);
      toast.success(`Subscription revoked for ${user.full_name}`);
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantFreeSubscription = async () => {
    // Grant free 30-day subscription
    setLoading(true);
    try {
      await api.put(`/admin/users/${user.id}/activate?duration_days=30`);
      toast.success(`Free 30-day subscription granted to ${user.full_name}`);
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant free subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleClearSessions = async () => {
    setLoading(true);
    try {
      const response = await api.delete(`/admin/users/${user.id}/sessions`);
      toast.success(`Cleared ${response.data.sessions_cleared} session(s) for ${user.full_name}`);
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleSendResetLink = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/admin/users/${user.id}/send-reset-link`);
      toast.success(response.data.message);
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUserConfirmed = async () => {
    console.log('handleDeleteUserConfirmed called for user:', user.id);
    
    setLoading(true);
    setShowDeleteConfirm(false);
    
    try {
      console.log('Making API call to delete user:', user.id);
      const token = localStorage.getItem('token');
      console.log('Token exists:', !!token);
      
      const response = await api.delete(`/admin/users/${user.id}`);
      console.log('Delete response received:', response.data);
      
      toast.success(`User ${user.full_name} deleted successfully!`);
      
      // Close modal and refresh user list
      console.log('Closing modal with refresh flag');
      onClose(true);
    } catch (error) {
      console.error('Delete user error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete user';
      toast.error(`Delete failed: ${errorMessage}`);
    } finally {
      setLoading(false);
      console.log('Delete operation completed, loading set to false');
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage User</DialogTitle>
          <DialogDescription>
            {user.full_name} ({user.email})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* User Info */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Role:</span>
              <span className="text-sm font-medium">{user.role}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Status:</span>
              <span className="text-sm font-medium">{user.subscription_status}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Registered:</span>
              <span className="text-sm font-medium">
                {new Date(user.created_at).toLocaleDateString()}
              </span>
            </div>
            {user.trial_end_date && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Trial Ends:</span>
                <span className="text-sm font-medium">
                  {new Date(user.trial_end_date).toLocaleDateString()}
                </span>
              </div>
            )}
            {user.subscription_end_date && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Subscription Ends:</span>
                <span className="text-sm font-medium">
                  {new Date(user.subscription_end_date).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>

          {/* Referral Information */}
          {(user.referral_token || user.referred_by) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 text-blue-700 font-semibold">
                <Gift className="w-4 h-4" />
                <span className="text-sm">Referral Information</span>
              </div>
              
              {user.referral_token && (
                <div className="bg-white rounded p-3 space-y-1">
                  <p className="text-xs text-gray-600">This User's Referral Code:</p>
                  <p className="font-mono font-bold text-emerald-600 text-sm">{user.referral_token}</p>
                  <p className="text-xs text-gray-500">Share this code to refer new users</p>
                </div>
              )}
              
              {user.referred_by && (
                <div className="bg-white rounded p-3 space-y-1">
                  <p className="text-xs text-gray-600">Referred By:</p>
                  <p className="font-mono font-bold text-purple-600 text-sm">{user.referred_by}</p>
                  {user.referral_date && (
                    <p className="text-xs text-gray-500">
                      Used on: {new Date(user.referral_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action Selector */}
          <div>
            <Label>Select Action</Label>
            <Select 
              value={action} 
              onValueChange={(value) => {
                console.log('Action selected:', value);
                setAction(value);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose an action..." />
              </SelectTrigger>
              <SelectContent>
                {/* Alphabetically ordered actions */}
                {user.email !== 'maridjo@gmail.com' && !user.is_banned && (
                  <SelectItem value="ban">Ban User</SelectItem>
                )}
                <SelectItem value="clear-sessions">Clear Sessions</SelectItem>
                {isSuperuser && user.email !== 'maridjo@gmail.com' && (
                  <SelectItem value="delete">Delete User (Permanent)</SelectItem>
                )}
                <SelectItem value="extend">Extend Trial</SelectItem>
                <SelectItem value="generate-code">Generate Activation Code</SelectItem>
                <SelectItem value="activate">Grant Full Access (Manual)</SelectItem>
                {isSuperuser && (
                  <SelectItem value="free">Grant Free 30-Day Subscription</SelectItem>
                )}
                {user.role !== 'admin' && (
                  <SelectItem value="promote">Make Admin</SelectItem>
                )}
                {isSuperuser && user.role === 'admin' && (
                  <SelectItem value="demote">Remove Admin Role</SelectItem>
                )}
                {isSuperuser && user.subscription_status === 'active' && (
                  <SelectItem value="revoke">Revoke Subscription</SelectItem>
                )}
                <SelectItem value="reset-password">Send Password Reset Link</SelectItem>
                {user.email !== 'maridjo@gmail.com' && user.is_banned && (
                  <SelectItem value="unban">Unban User</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          
          {/* Debug info */}
          {action && (
            <div className="text-xs text-gray-500 p-2 bg-gray-50 rounded">
              Current action: <span className="font-mono font-semibold">{action}</span>
            </div>
          )}

          {/* Demote from Admin Form */}
          {action === 'demote' && (
            <div className="space-y-4 p-4 border border-red-200 rounded-lg bg-red-50">
              <div className="flex items-center gap-2 text-red-600">
                <Shield className="w-5 h-5" />
                <h3 className="font-semibold">Remove Admin Role</h3>
              </div>
              <p className="text-sm text-gray-700">
                This will remove admin privileges from <strong>{user.full_name}</strong>. They will:
              </p>
              <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
                <li>Lose access to Admin Dashboard</li>
                <li>Unable to manage users</li>
                <li>Unable to view payments and analytics</li>
                <li>Revert to their subscription status (trial/active/expired)</li>
              </ul>
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-xs text-yellow-800">
                  <strong>⚠️ Note:</strong> Only the superuser (maridjo@gmail.com) can perform this action.
                </p>
              </div>
              <Button 
                onClick={handleDemoteFromAdmin} 
                disabled={loading} 
                variant="destructive"
                className="w-full"
              >
                {loading ? 'Processing...' : 'Remove Admin Role'}
              </Button>
            </div>
          )}

          {/* Promote to Admin Form */}
          {action === 'promote' && (
            <div className="space-y-4 p-4 border border-purple-200 rounded-lg bg-purple-50">
              <div className="flex items-center gap-2 text-purple-600">
                <Shield className="w-5 h-5" />
                <h3 className="font-semibold">Make Admin</h3>
              </div>
              <p className="text-sm text-gray-700">
                This will grant <strong>{user.full_name}</strong> full administrative privileges, including:
              </p>
              <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
                <li>Access to Admin Dashboard</li>
                <li>Manage all users</li>
                <li>View payments and analytics</li>
                <li>Promote other users to admin</li>
              </ul>
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-xs text-yellow-800">
                  <strong>⚠️ Warning:</strong> Admin privileges cannot be easily revoked. Only promote trusted users.
                </p>
              </div>
              <Button 
                onClick={handlePromoteToAdmin} 
                disabled={loading} 
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {loading ? 'Processing...' : 'Promote to Admin'}
              </Button>
            </div>
          )}

          {/* Activate Subscription Form */}
          {action === 'activate' && (
            <div className="space-y-4 p-4 border-2 border-emerald-200 rounded-lg bg-emerald-50">
              <div className="flex items-center gap-2 text-emerald-600">
                <UserCheck className="w-5 h-5" />
                <h3 className="font-semibold">Grant Full Access (Manual Activation)</h3>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-900">
                  <strong>💡 Manual Activation:</strong> Since payment gateway integration is not yet available, 
                  you can manually grant users full access to the platform.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Select Access Duration</Label>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 Days (1 Month)</SelectItem>
                    <SelectItem value="90">90 Days (3 Months)</SelectItem>
                    <SelectItem value="180">180 Days (6 Months)</SelectItem>
                    <SelectItem value="365">365 Days (1 Year) ⭐</SelectItem>
                    <SelectItem value="730">730 Days (2 Years)</SelectItem>
                    <SelectItem value="36500">Lifetime Access (Permanent) 👑</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  User will be upgraded from Trial to Subscriber with active subscription status.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-700 mb-1">What happens:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>✓ User role changes from "Trial" to "Subscriber"</li>
                  <li>✓ Subscription status becomes "Active"</li>
                  <li>✓ User gets unlimited access to all features</li>
                  <li>✓ Trial restrictions are removed</li>
                </ul>
              </div>

              <Button 
                onClick={handleActivateSubscription} 
                disabled={loading} 
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3"
              >
                {loading ? 'Processing...' : '✓ Grant Full Access'}
              </Button>
            </div>
          )}

          {/* Generate Activation Code Form */}
          {action === 'generate-code' && (
            <div className="space-y-4 p-4 border-2 border-emerald-200 rounded-lg bg-emerald-50">
              <div className="flex items-center gap-2 text-emerald-600">
                <Key className="w-5 h-5" />
                <h3 className="font-semibold">Generate Activation Code</h3>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-900">
                  <strong>ℹ️ Info:</strong> This will generate a unique activation code and send it to {user.full_name}'s email. 
                  The user must enter this code in their Settings to activate their subscription.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 font-semibold">Select Subscription Duration</Label>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 Days (1 Month)</SelectItem>
                    <SelectItem value="90">90 Days (3 Months)</SelectItem>
                    <SelectItem value="180">180 Days (6 Months)</SelectItem>
                    <SelectItem value="365">365 Days (1 Year) ⭐</SelectItem>
                    <SelectItem value="730">730 Days (2 Years)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  The activation code will be valid for 30 days from generation.
                </p>
              </div>

              {generatedCode && (
                <div className="bg-white border-2 border-emerald-300 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 mb-2">Generated Activation Code:</p>
                  <p className="text-2xl font-bold text-emerald-600 tracking-wider">{generatedCode}</p>
                  <p className="text-xs text-gray-500 mt-2">This code has been sent to {user.email}</p>
                </div>
              )}

              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-700 mb-1">What happens:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>✓ Unique activation code is generated</li>
                  <li>✓ Code is emailed to the user</li>
                  <li>✓ User enters code in Settings to activate</li>
                  <li>✓ Subscription starts when user activates</li>
                </ul>
              </div>

              <Button 
                onClick={handleGenerateActivationCode} 
                disabled={loading || generatedCode} 
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3"
              >
                {loading ? 'Generating...' : generatedCode ? '✓ Code Generated' : '🔑 Generate & Send Code'}
              </Button>
            </div>
          )}

          {/* Grant Free Subscription Form */}
          {action === 'free' && (
            <div className="space-y-4 p-4 border-2 border-purple-200 rounded-lg bg-purple-50">
              <div className="flex items-center gap-2 text-purple-600">
                <Gift className="w-5 h-5" />
                <h3 className="font-semibold">Grant Free 30-Day Subscription</h3>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-900">
                  <strong>⚠️ Superuser Only:</strong> This will grant a FREE 30-day subscription to this user. 
                  Use this for special cases like beta testers, promotions, or customer service resolutions.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-700 mb-1">What happens:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>✓ User gets 30 days of free access</li>
                  <li>✓ Subscription status becomes "Active"</li>
                  <li>✓ No payment required</li>
                  <li>✓ User role changes to "Subscriber"</li>
                </ul>
              </div>

              <Button 
                onClick={handleGrantFreeSubscription} 
                disabled={loading} 
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3"
              >
                {loading ? 'Processing...' : '🎁 Grant Free 30-Day Access'}
              </Button>
            </div>
          )}

          {/* Revoke Subscription Form */}
          {action === 'revoke' && (
            <div className="space-y-4 p-4 border-2 border-red-200 rounded-lg bg-red-50">
              <div className="flex items-center gap-2 text-red-600">
                <UserX className="w-5 h-5" />
                <h3 className="font-semibold">Revoke Subscription</h3>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-900">
                  <strong>⚠️ Warning:</strong> This will immediately revoke {user.full_name}'s active subscription. 
                  They will lose access to all premium features.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-700 mb-1">What happens:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>✗ Subscription status changes to "Expired"</li>
                  <li>✗ User loses access immediately</li>
                  <li>✗ Cannot access premium features</li>
                  <li>✗ Subscription end date set to now</li>
                </ul>
              </div>

              <Button 
                onClick={handleRevokeSubscription} 
                disabled={loading} 
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3"
              >
                {loading ? 'Processing...' : '✗ Revoke Subscription'}
              </Button>
            </div>
          )}

          {/* Clear Sessions Form */}
          {action === 'clear-sessions' && (
            <div className="space-y-4 p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
              <div className="flex items-center gap-2 text-blue-600">
                <Shield className="w-5 h-5" />
                <h3 className="font-semibold">Clear Sessions</h3>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-900">
                  <strong>ℹ️ Info:</strong> This will clear all active sessions for {user.full_name}. 
                  Use this when a user reports being locked out due to the 2-session limit.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-700 mb-1">What happens:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>✓ All active sessions will be cleared</li>
                  <li>✓ User can log in again</li>
                  <li>✓ Does not affect user's subscription or data</li>
                  <li>✓ Useful when user closes browser without logging out</li>
                </ul>
              </div>

              <Button 
                onClick={handleClearSessions} 
                disabled={loading} 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3"
              >
                {loading ? 'Clearing...' : '🔄 Clear All Sessions'}
              </Button>
            </div>
          )}

          {/* Send Password Reset Link Form */}
          {action === 'reset-password' && (
            <div className="space-y-4 p-4 border-2 border-amber-200 rounded-lg bg-amber-50">
              <div className="flex items-center gap-2 text-amber-600">
                <KeyRound className="w-5 h-5" />
                <h3 className="font-semibold">Send Password Reset Link</h3>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-700">
                  This will send a password reset email to <strong>{user.email}</strong> with a reset code that expires in 24 hours.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-700 mb-1">The user will receive:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>✓ Email with a 6-digit reset code</li>
                  <li>✓ Instructions to reset their password</li>
                  <li>✓ Code valid for 24 hours</li>
                </ul>
              </div>

              <Button 
                onClick={handleSendResetLink} 
                disabled={loading} 
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3"
              >
                {loading ? 'Sending...' : '📧 Send Password Reset Email'}
              </Button>
            </div>
          )}

          {/* Extend Trial Form */}
          {action === 'extend' && (
            <div className="space-y-4 p-4 border rounded-lg">
              <div className="flex items-center gap-2 text-blue-600">
                <Clock className="w-5 h-5" />
                <h3 className="font-semibold">Extend Trial Period</h3>
              </div>
              <div>
                <Label>Additional Days</Label>
                <Input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="e.g., 7"
                />
              </div>
              <Button onClick={handleExtendTrial} disabled={loading} className="w-full">
                {loading ? 'Processing...' : 'Extend Trial'}
              </Button>
            </div>
          )}

          {/* Ban User Form */}
          {action === 'ban' && (
            <div className="space-y-4 p-4 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-600">
                <Ban className="w-5 h-5" />
                <h3 className="font-semibold">Ban User</h3>
              </div>
              <div>
                <Label>Reason for Ban</Label>
                <Textarea
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  placeholder="Explain why this user is being banned..."
                  rows={3}
                />
              </div>
              <Button 
                onClick={handleBanUser} 
                disabled={loading} 
                variant="destructive"
                className="w-full"
              >
                {loading ? 'Processing...' : 'Ban User'}
              </Button>
            </div>
          )}

          {/* Unban User */}
          {action === 'unban' && (
            <div className="space-y-4 p-4 border rounded-lg">
              <div className="flex items-center gap-2 text-emerald-600">
                <UserCheck className="w-5 h-5" />
                <h3 className="font-semibold">Unban User</h3>
              </div>
              <p className="text-sm text-gray-600">
                This will remove the ban and allow the user to access the platform again.
                {user.banned_reason && (
                  <span className="block mt-2 font-medium">
                    Current ban reason: {user.banned_reason}
                  </span>
                )}
              </p>
              <Button onClick={handleUnbanUser} disabled={loading} className="w-full">
                {loading ? 'Processing...' : 'Unban User'}
              </Button>
            </div>
          )}

          {/* Delete User */}
          {action === 'delete' && (
            <div className="space-y-4 p-4 border border-red-200 rounded-lg bg-red-50">
              <div className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                <h3 className="font-semibold">Delete User (Super-User Only)</h3>
              </div>
              
              {!isSuperuser ? (
                <div className="space-y-2">
                  <p className="text-sm text-red-600 font-semibold">
                    🔒 Only the super-user can delete accounts
                  </p>
                  <p className="text-sm text-gray-600">
                    User deletion is restricted to the super-user (maridjo@gmail.com) for security and data management purposes.
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <p className="text-sm text-red-600 font-bold">
                      ⚠️ PERMANENT DELETION WARNING
                    </p>
                    <p className="text-sm text-gray-700">
                      This will <strong>permanently and completely delete</strong> this user:
                    </p>
                    <ul className="text-sm text-gray-700 list-disc list-inside space-y-1 ml-2">
                      <li>User account will be removed from the database</li>
                      <li>All bookmarks, notes, and highlights will be deleted</li>
                      <li>Email address will become available for new registration</li>
                      <li>This action <strong>CANNOT be undone</strong></li>
                    </ul>
                    <div className="bg-white p-3 rounded border-2 border-red-400 mt-2">
                      <p className="text-xs font-semibold text-gray-700">User to permanently delete:</p>
                      <p className="text-sm text-gray-900 font-medium">{user.full_name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                      <p className="text-xs text-gray-500">Role: {user.role}</p>
                    </div>
                  </div>
                  {!showDeleteConfirm ? (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Delete button clicked - showing confirmation');
                        setShowDeleteConfirm(true);
                      }} 
                      disabled={loading}
                      type="button"
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      🗑️ Permanently Delete User
                    </button>
                  ) : (
                    <div className="space-y-3 bg-yellow-50 border-2 border-yellow-400 p-4 rounded-lg">
                      <p className="text-sm font-bold text-red-900 text-center">
                        ⚠️ FINAL CONFIRMATION REQUIRED
                      </p>
                      <p className="text-sm text-red-800 text-center font-medium">
                        This will PERMANENTLY delete <strong>{user.email}</strong> from the database.
                        Are you absolutely certain?
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('User clicked CANCEL');
                            setShowDeleteConfirm(false);
                            toast.info('Delete cancelled');
                          }}
                          type="button"
                          className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-md transition-colors"
                        >
                          ✖ Cancel
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('User clicked CONFIRM DELETE');
                            handleDeleteUserConfirmed();
                          }}
                          disabled={loading}
                          type="button"
                          className="flex-1 bg-red-700 hover:bg-red-800 text-white font-semibold py-2 px-4 rounded-md disabled:opacity-50 transition-colors"
                        >
                          {loading ? 'Deleting...' : '✓ YES, DELETE PERMANENTLY'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
